/**
 * streamingMarkdown.js
 * Incremental markdown rendering during streaming with rAF batching.
 * Renders markdown progressively like ChatGPT/Claude — no blink, no flicker.
 */

import { enhanceCodeBlocks } from './codeBlocks.js?v=302';
import { enhanceImages } from './media/imageRenderer.js?v=302';
import { enhanceVideoLinks } from './media/youtubeEmbed.js?v=302';

// WeakMap to store per-element state (previous HTML, pending rAF id)
const elementState = new WeakMap();

// ---------------------------------------------------------------------------
// rewriteLocalMediaUrls
// ---------------------------------------------------------------------------
// Local agents on this box (Hermes at ~/.hermes/, OpenClaw at ~/.openclaw/)
// emit markdown images pointing at absolute filesystem paths. The browser
// can't fetch those directly. nginx routes /media/?p=<path> to a small media
// server on :8892 that serves files inside an allowlisted root. This helper
// rewrites src/href attrs in parsed HTML so the browser actually requests
// that endpoint.
//
// Recognised input forms (per allowed root):
//   /home/denny/.hermes/.../foo.png
//   /home/denny/.openclaw/.../foo.png
//   ~/.hermes/.../foo.png            (expanded)
//   ~/.openclaw/.../foo.png          (expanded)
//   file:///home/denny/.hermes/.../foo.png  (file:// prefix stripped)
//   file:///home/denny/.openclaw/.../foo.png
const _MEDIA_ALLOWED_ROOTS = [
  '/home/denny/.hermes/',
  '/home/denny/.openclaw/',
];
const _MEDIA_FILE_PREFIX = 'file://';
const _MEDIA_HOME_PREFIXES = {
  '~/.hermes/':   '/home/denny/.hermes/',
  '~/.openclaw/': '/home/denny/.openclaw/',
};
const _MEDIA_EXT_RE =
  /\.(?:png|jpe?g|gif|webp|svg|bmp|avif|ico|mp4|mov|webm|mkv|mp3|wav|ogg|m4a|flac)(?:\?[^"']*)?$/i;
const _MEDIA_ATTR_RE = /(src|href)=("|')([^"']+)\2/g;

function _normaliseLocalMediaPath(val) {
  let path = val;
  if (path.startsWith(_MEDIA_FILE_PREFIX)) {
    path = path.slice(_MEDIA_FILE_PREFIX.length);
  }
  for (const [pfx, real] of Object.entries(_MEDIA_HOME_PREFIXES)) {
    if (path.startsWith(pfx)) {
      path = real + path.slice(pfx.length);
      break;
    }
  }
  return path;
}

export function rewriteLocalMediaUrls(html) {
  if (!html || typeof html !== 'string') return html;
  return html.replace(_MEDIA_ATTR_RE, (full, attr, q, val) => {
    const path = _normaliseLocalMediaPath(val);
    const underAllowedRoot = _MEDIA_ALLOWED_ROOTS.some((r) => path.startsWith(r));
    if (!underAllowedRoot) return full;
    if (!_MEDIA_EXT_RE.test(path)) return full;
    return `${attr}=${q}/media/?p=${encodeURIComponent(path)}${q}`;
  });
}

function isMarkdownEscaped(text, index) {
  // In markdown, an odd number of consecutive backslashes escapes the next char.
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && text[i] === '\\'; i--) {
    backslashes++;
  }
  return backslashes % 2 === 1;
}

function isListBulletAsterisk(text, index) {
  // Detect list bullets like "* item" or "  * item".
  // We intentionally exclude these from emphasis balancing.
  const prevNewline = text.lastIndexOf('\n', index - 1);
  const lineStart = prevNewline === -1 ? 0 : prevNewline + 1;
  for (let i = lineStart; i < index; i++) {
    const ch = text[i];
    if (ch !== ' ' && ch !== '\t') return false;
  }
  const next = text[index + 1];
  return next === ' ' || next === '\t';
}

function isWordChar(ch) {
  return !!ch && /[A-Za-z0-9]/.test(ch);
}

function countSingleEmphasisMarkers(text, marker) {
  let singles = 0;
  for (let i = 0; i < text.length; ) {
    if (text[i] !== marker || isMarkdownEscaped(text, i)) {
      i++;
      continue;
    }

    let j = i;
    while (j < text.length && text[j] === marker && !isMarkdownEscaped(text, j)) {
      j++;
    }

    const runLen = j - i;

    if (marker === '*' && runLen === 1 && isListBulletAsterisk(text, i)) {
      i = j;
      continue;
    }

    if (marker === '_') {
      const prev = text[i - 1];
      const next = text[j];
      // Ignore underscores used inside words, e.g. snake_case.
      if (isWordChar(prev) && isWordChar(next)) {
        i = j;
        continue;
      }
    }

    // Treat pairs as bold, leftover single as italic/emphasis marker.
    singles += runLen % 2;
    i = j;
  }
  return singles;
}

function holdBackIncompleteTail(text) {
  if (!text) return text;

  let end = text.length;

  // Avoid rendering unpaired surrogate halves for a frame.
  if (end > 0) {
    const last = text.charCodeAt(end - 1);
    if (last >= 0xd800 && last <= 0xdbff) {
      end -= 1;
    } else if (last >= 0xdc00 && last <= 0xdfff) {
      const prev = end > 1 ? text.charCodeAt(end - 2) : 0;
      if (!(prev >= 0xd800 && prev <= 0xdbff)) {
        end -= 1;
      }
    }
  }

  // Avoid rendering incomplete HTML entities split across chunks (e.g. "&amp" + ";").
  const lookback = 16;
  const start = Math.max(0, end - lookback);
  const tail = text.slice(start, end);
  const amp = tail.lastIndexOf('&');
  if (amp !== -1) {
    const afterAmp = tail.slice(amp);
    if (!afterAmp.includes(';')) {
      const looksLikeEntity =
        /^&(#\d{0,8}|#x[0-9A-Fa-f]{0,8}|[A-Za-z][A-Za-z0-9]{0,31})?$/.test(afterAmp);
      if (looksLikeEntity) {
        end = start + amp;
      }
    }
  }

  return end === text.length ? text : text.slice(0, end);
}

/**
 * Close incomplete markdown syntax so marked.parse() doesn't produce broken HTML.
 * Simplified port of HuggingFace chat-ui's parseIncompleteMarkdown logic.
 * @param {string} text - Raw markdown text (potentially mid-stream)
 * @returns {string} - Text with unclosed syntax closed
 */
function closeIncompleteMarkdown(text) {
  if (!text) return text;

  let result = text;

  // 1. Close unclosed code fences
  const fenceCount = (result.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) {
    result += '\n```';
  }

  // If inside a code fence, don't process inline formatting
  if (fenceCount % 2 === 1) {
    return result;
  }

  // 2. Close unclosed bold (**) 
  const boldCount = (result.match(/\*\*/g) || []).length;
  if (boldCount % 2 === 1) {
    result += '**';
  }

  // 3. Close unclosed strikethrough (~~)
  const strikeCount = (result.match(/~~/g) || []).length;
  if (strikeCount % 2 === 1) {
    result += '~~';
  }

  // 4. Close unclosed inline code (single backtick, not part of fence)
  // Count backticks not part of triple backticks
  let singleBacktickCount = 0;
  for (let i = 0; i < result.length; i++) {
    if (result[i] === '`') {
      // Check if part of triple backtick
      const isTriple = result.substring(i, i + 3) === '```' ||
                       (i > 0 && result.substring(i - 1, i + 2) === '```') ||
                       (i > 1 && result.substring(i - 2, i + 1) === '```');
      if (!isTriple) singleBacktickCount++;
    }
  }
  if (singleBacktickCount % 2 === 1) {
    result += '`';
  }

  // 5. Close unclosed italics/emphasis markers (* and _)
  // Count single markers (leftovers after pairing into bold) and close if odd.
  const singleAsteriskCount = countSingleEmphasisMarkers(result, '*');
  if (singleAsteriskCount % 2 === 1) {
    result += '*';
  }

  const singleUnderscoreCount = countSingleEmphasisMarkers(result, '_');
  if (singleUnderscoreCount % 2 === 1) {
    result += '_';
  }

  // 6. Handle incomplete links [text]( or [text
  // Remove incomplete link syntax at end of string
  result = result.replace(/\[([^\]]*)\]\([^)]*$/, '[$1]()');
  result = result.replace(/\[([^\]]*)$/, '$1');

  return result;
}

/**
 * Update streaming markdown content with rAF batching.
 * Only updates DOM when HTML actually changes, max once per frame.
 * @param {HTMLElement} contentDiv - The .message-content element
 * @param {string} fullText - The complete bot text so far
 */
export function updateStreamingMarkdown(contentDiv, fullText) {
  // Get or create element state
  let state = elementState.get(contentDiv);
  if (!state) {
    state = { prevHtml: '', rafId: null, lastText: '' };
    elementState.set(contentDiv, state);
  }

  // Store latest text for the rAF callback
  state.lastText = fullText;

  // Skip if rAF is already pending (batching)
  if (state.rafId !== null) return;

  // Schedule render on next animation frame
  state.rafId = requestAnimationFrame(() => {
    state.rafId = null;

    if (!contentDiv.isConnected) {
      elementState.delete(contentDiv);
      return;
    }

    try {
      const stableText = holdBackIncompleteTail(state.lastText);
      // Close incomplete markdown syntax
      const sanitized = closeIncompleteMarkdown(stableText);

      // Parse to HTML
      const newHtml = rewriteLocalMediaUrls(window.marked.parse(sanitized));

      // Only update DOM if HTML changed
      if (newHtml !== state.prevHtml) {
        contentDiv.innerHTML = newHtml;
        state.prevHtml = newHtml;

        // Highlight only new/unprocessed code blocks
        const unprocessed = contentDiv.querySelectorAll('pre code:not(.prism-highlighted)');
        if (unprocessed.length > 0) {
          unprocessed.forEach(block => {
            window.Prism.highlightElement(block);
            block.classList.add('prism-highlighted');
          });
        }

        // Enhance code blocks (idempotent - checks for existing copy buttons)
        enhanceCodeBlocks(contentDiv);

        // Clear video flag since innerHTML was replaced — old embeds are gone,
        // and more text (including new video URLs) may have arrived.
        delete contentDiv.dataset.videoEnhanced;

        // Enhance media (idempotent per-element for images, flag-based for video)
        enhanceImages(contentDiv);
        enhanceVideoLinks(contentDiv);
      }
    } catch (e) {
      // Fallback: show plain text if parsing fails
      contentDiv.textContent = state.lastText;
    }
  });
}

/**
 * Cancel any pending rAF and clean up state for an element.
 * Call this when streaming ends or is aborted.
 * @param {HTMLElement} contentDiv - The .message-content element
 */
export function cancelStreamingMarkdown(contentDiv) {
  const state = elementState.get(contentDiv);
  if (state) {
    if (state.rafId !== null) {
      cancelAnimationFrame(state.rafId);
    }
    elementState.delete(contentDiv);
  }
}
